import {type ReactNode} from 'react';
import {useOnBoardingContext} from '../../../../context/onBoarding/OnBoardingContextProvider';
import {SVG} from '../../../../constants/assetLists';
import AppButton from '../../../../components/common/AppButton';
import AppSwitch from '../../../../components/common/AppSwitch';
import {
  handleActivateOnBoardingDraftService,
  OnBoardingDraftResponse,
} from '../../../../services/on-boarding';
import {
  AdminLocationType,
  handleUpdateAdminLocationStatusService,
  OrganizationState,
} from '../../../../services/organizations';
import {
  BaseNodeFields,
  BranchNodeFormValues,
  NetworkNodeFormValues,
  SubBranchNodeFormValues,
} from '../NetworkDetails/types';
import {
  nodeRequiredDetailsSchema,
  nodeSetupCompleteSchema,
} from '../NetworkDetails/networkDetailsSchema';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useNavigate} from 'react-router';
import moment from 'moment';
import QUERY_KEYS from '../../../../constants/queryKeys';
import {isSuperAdminOnly} from '../../../../utils';
import { useSnackbar } from '../../../../context/SnackbarContextProvider';

type DraftDetailsProps = {
  draftData: OnBoardingDraftResponse | undefined;
  isOnBoardingCompleted: boolean;
  isDraftValid: boolean;
  shouldResyncOnboarding: boolean;
};

type NodeLevel = 'network' | 'branch' | 'subBranch';

const NODE_LEVEL_LABELS: Record<NodeLevel, string> = {
  network: 'רשת',
  branch: 'סניף',
  subBranch: 'תת סניף',
};

const NODE_LEVEL_LOCATION_TYPE: Record<NodeLevel, AdminLocationType> = {
  network: 'client',
  branch: 'branch',
  subBranch: 'subBranch',
};

type NodeDetailCardProps = {
  node: BaseNodeFields & {id: string};
  level: NodeLevel;
  isStatusUpdating: boolean;
  onStatusChange: (
    node: BaseNodeFields & {id: string},
    level: NodeLevel,
    checked: boolean,
  ) => void;
};

function DetailRow({
  label,
  value,
  isPartial,
}: {
  label: string;
  value: number;
  isPartial: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      {isPartial && (
        <span className="w-[14px] h-[14px] rounded-full bg-[#E52055]" />
      )}
      <p className="m-0 text-xl font-medium text-darkerGray">{label}</p>
      <p className={`m-0 text-xl font-medium ${
          (value === 0 && isPartial) ? 'text-raspberryRed' : 'text-darkerGray'
        }`}>{value}</p>
    </div>
  );
}

function NodeDetailCard({
  node,
  level,
  isStatusUpdating,
  onStatusChange,
}: NodeDetailCardProps) {
  const validationResult = nodeSetupCompleteSchema.safeParse(node);
  const isRequiredDetailsComplete =
    nodeRequiredDetailsSchema.safeParse(node).success;
  const isActive = node.status === 'active';
  const partialFieldNames = new Set(
    validationResult.success
      ? []
      : validationResult.error.issues
          .map(issue => issue.path[0])
          .filter((field): field is string => typeof field === 'string'),
  );
  const isPartialField = (field: keyof BaseNodeFields) =>
    partialFieldNames.has(field);
  const isShiftsPartial =
    isPartialField('shifts') ||
    isPartialField('operatingHours') ||
    isPartialField('checklistNotesReset');

  return (
    <div className="shrink-0 p-2 border border-gray70 rounded-lg flex flex-col gap-1.5 w-auto h-[350px]">
      <div className="flex items-center gap-1">
        <p className="m-0 text-xl font-medium text-darkerGray">
          {NODE_LEVEL_LABELS[level]}:
        </p>
        <p className="m-0 text-xl font-bold text-darkerGray">
          {node?.name || ''}
        </p>
      </div>

      {node.status && node.status !== 'draft' && isSuperAdminOnly() && (
        <div className="flex items-center gap-2">
          <AppSwitch
            checked={isActive}
            outlined={!isActive}
            disabled={isStatusUpdating || !!node.isArchived}
            onChange={event => onStatusChange(node, level, event.target.checked)}
          />
          <p className="m-0 text-xl font-medium text-darkerGray">
            {isActive ? 'פעיל' : 'לא פעיל'}
          </p>
        </div>
      )}


      <div className="flex items-center gap-1">
        {!isRequiredDetailsComplete && (
        <span className="w-[14px] h-[14px] rounded-full bg-[#E52055]" />
      )}
        <p className="m-0 text-xl font-medium text-darkerGray">סטטוס:</p>
        <p
          className={`m-0 text-xl font-medium ${isRequiredDetailsComplete ? 'text-green' : 'text-raspberryRed'}`}>
          {isRequiredDetailsComplete ? 'הושלם' : 'חלקי'}
        </p>
      </div>

      <DetailRow
        label="הגדרת מחלקות:"
        value={node?.departments?.length || 0}
        isPartial={isPartialField('departments')}
      />

      <DetailRow
        label="תפקידים והרשאות:"
        value={node?.roles?.length || 0}
        isPartial={isPartialField('roles')}
      />

      <DetailRow
        label="משמרות:"
        value={node?.shifts?.length || 0}
        isPartial={isShiftsPartial}
      />

      <DetailRow
        label="אזורים:"
        value={node?.zones?.length || 0}
        isPartial={isPartialField('zones')}
      />

      <DetailRow
        label="משתמשים:"
        value={node?.users?.length || 0}
        isPartial={isPartialField('users')}
      />

      <DetailRow
        label="צ'קליסטים:"
        value={node?.checklists?.length || 0}
        isPartial={isPartialField('checklists')}
      />

      <DetailRow
        label="רגולציות:"
        value={node?.regulations?.length || 0}
        isPartial={isPartialField('regulations')}
      />
    </div>
  );
}

type GeneralDetailsCardProps = {
  organizationName: string;
  branchCount: number;
  activatedAt?: Date;
  updatedAt?: Date;
};

function GeneralDetailsCard({
  organizationName,
  branchCount,
  activatedAt,
  updatedAt,
}: GeneralDetailsCardProps) {
  return (
    <div className="shrink-0 w-[450px] h-auto p-3 border border-gray70 rounded-lg bg-[#026A6E] flex flex-col justify-center gap-2">
      <p className="m-0 text-xl font-bold text-lightBlueTint">פרטים כלליים</p>

      <div className="flex items-center gap-1">
        <p className="m-0 text-xl font-medium text-lightBlueTint">שם הארגון:</p>
        <p className="m-0 text-xl font-medium text-lightBlueTint">
          {organizationName}
        </p>
      </div>

      <div className="flex items-center gap-1">
        <p className="m-0 text-xl font-medium text-lightBlueTint">
          מספר סניפים:
        </p>
        <p className="m-0 text-xl font-medium text-lightBlueTint">
          {branchCount}
        </p>
      </div>

      <div className="flex items-center gap-1">
        <p className="m-0 text-xl font-medium text-lightBlueTint">
          תאריך הפעלה:
        </p>
        <p className="m-0 text-xl font-medium text-lightBlueTint">
          {activatedAt ? moment(activatedAt).format('DD/MM/YYYY HH:mm') : ''}
        </p>
      </div>

      <div className="flex items-center gap-1">
        <p className="m-0 text-xl font-medium text-lightBlueTint">
          תאריך עדכון אחרון:
        </p>
        <p className="m-0 text-xl font-medium text-lightBlueTint">
          {updatedAt ? moment(updatedAt).format('DD/MM/YYYY HH:mm') : ''}
        </p>
      </div>
    </div>
  );
}

function DraftDetails({
  draftData,
  isOnBoardingCompleted,
  isDraftValid,
  shouldResyncOnboarding,
}: DraftDetailsProps) {
  const queryClient = useQueryClient();

  const { setCurrentStep } = useOnBoardingContext();
  // const [persistedDraft, setPersistedDraft] = useState<NetworkDetailsFormValues | null>(null);

  const navigate = useNavigate();

  const { showSnackbar } = useSnackbar();

  // useEffect(() => {
  //   let isMounted = true;

  //   const loadPersistedDraft = async () => {
  //     const savedDraft = await getPersistedDraftData();
  //     if (isMounted) {
  //       setPersistedDraft(savedDraft);
  //     }
  //   };

  //   void loadPersistedDraft();
  //   window.addEventListener('focus', loadPersistedDraft);
  //   document.addEventListener('visibilitychange', loadPersistedDraft);

  //   return () => {
  //     isMounted = false;
  //     window.removeEventListener('focus', loadPersistedDraft);
  //     document.removeEventListener('visibilitychange', loadPersistedDraft);
  //   };
  // }, []);

  const isActive =
    isOnBoardingCompleted && isDraftValid && !shouldResyncOnboarding;
  // const persistedNetworks = persistedDraft?.network ?? [];
  // const networks =
  //   persistedNetworks.length > 0
  //     ? persistedNetworks
  //     : draftData?.data ?? [];

  const networks = draftData?.data ?? [];

  const organizationName =
    networks
      .map(network => network.name)
      .filter(Boolean)
      .join(', ') || '';

  const branchCount = networks.reduce(
    (total, network) => total + (network.branches?.length ?? 0),
    0,
  );

  const handleNextStep = () => {
    setCurrentStep(prevStep => prevStep + 1);
  };

  const handleNavigateToOperations = () => {
    navigate(-1);
  };

  const {mutate: handleActivateDraft, isPending: isActivatePending} =
    useMutation({
      mutationFn: handleActivateOnBoardingDraftService,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT],
        });
        handleNavigateToOperations();
      },
    });

	  const updateStatusMutation = useMutation({
	    mutationFn: handleUpdateAdminLocationStatusService,
	    onSuccess: (data) => {
	      queryClient.invalidateQueries({
	        queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT],
	      });
	      if (isSuperAdminOnly()) {
	        queryClient.invalidateQueries({
	          queryKey: [QUERY_KEYS.GET_ADMIN_ON_BOARDING_DRAFTS],
	        });
	      }
	      queryClient.invalidateQueries({
	        queryKey: [QUERY_KEYS.GET_USER_CLIENT],
	      });

        showSnackbar({
          message: data?.message || 'הסטטוס עודכן בהצלחה',
          type: 'success',
      });
	    },
	  });

  const handleStatusChange = (
    node: BaseNodeFields & {id: string},
    level: NodeLevel,
    checked: boolean,
  ) => {
    if (node.isArchived) return;

    const nextStatus: Exclude<OrganizationState, 'draft'> = checked
      ? 'active'
      : 'inactive';

    updateStatusMutation.mutate({
      locationType: NODE_LEVEL_LOCATION_TYPE[level],
      locationId: node.id,
      status: nextStatus,
    });
  };

  const renderButtons = () => {
    if (!isDraftValid) {
      return (
        <div className="flex items-center gap-5">
          <AppButton
            variant="custom"
            className="w-[198px] h-[64px] justify-center bg-linkBlue border-[4px] border-white text-white text-2xl font-bold hover:opacity-90"
            onClick={handleNextStep}>
            להמשך הגדרה
          </AppButton>
        </div>
      );
    }

    if (
      (!isOnBoardingCompleted && isDraftValid) ||
      (shouldResyncOnboarding && isDraftValid)
    ) {
      return (
        <div className="flex items-center gap-5">
          <AppButton
            className="w-[198px] h-[64px] justify-center !text-2xl !font-bold"
            onClick={handleNextStep}>
            להמשך הגדרה
          </AppButton>

          <AppButton
            variant="custom"
            className={`w-[198px] h-[64px] justify-center  border-[4px] border-white text-2xl font-bold hover:opacity-90 
              ${isActivatePending ? 'bg-[#76768033] cursor-not-allowed text-[#3C3C4373]' : 'bg-linkBlue text-white'}`}
            disabled={isActivatePending}
            isLoading={isActivatePending}
            onClick={() => handleActivateDraft()}>
            הפעלת המערכת
          </AppButton>
        </div>
      );
    }

    if (isActive) {
      return (
        <div className="flex items-center gap-5">
          <AppButton
            className="w-[198px] h-[64px] justify-center !text-2xl !font-bold"
            onClick={handleNextStep}>
            להמשך הגדרה
          </AppButton>
        </div>
      );
    }

    return null;
  };

  const renderNodeCards = (network: NetworkNodeFormValues) => {
    const cards: ReactNode[] = [
      <NodeDetailCard
        key={`network-${network.id}`}
        node={network}
        level="network"
        isStatusUpdating={updateStatusMutation.isPending}
        onStatusChange={handleStatusChange}
      />,
    ];

    network.branches?.forEach((branch: BranchNodeFormValues) => {
      cards.push(
        <NodeDetailCard
          key={`branch-${branch.id}`}
          node={branch}
          level="branch"
          isStatusUpdating={updateStatusMutation.isPending}
          onStatusChange={handleStatusChange}
        />,
      );

      branch.subBranches?.forEach((subBranch: SubBranchNodeFormValues) => {
        cards.push(
          <NodeDetailCard
            key={`subBranch-${subBranch.id}`}
            node={subBranch}
            level="subBranch"
            isStatusUpdating={updateStatusMutation.isPending}
            onStatusChange={handleStatusChange}
          />,
        );
      });
    });

    return cards;
  };

  return (
    <div className="flex-1 flex flex-col gap-3">
      <div
        className="flex flex-col items-center gap-3 py-3"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(7, 221, 202, 0.5) 0%, rgba(133, 255, 248, 0.5) 73.08%, rgba(39, 219, 215, 0.5) 100%)`,
        }}>
        <div className="w-[130px] h-[125px]">
          <img
            src={SVG.cheqqItTickIcon}
            alt="tick"
            className="w-full h-full object-contain"
          />
        </div>

        {renderButtons()}
      </div>

      <div className="w-full overflow-x-auto overflow-y-hidden pb-3 px-[45px]">
        <div className="flex items-stretch gap-[16px] w-max mx-auto">
          {isActive && (
            <GeneralDetailsCard
              organizationName={organizationName}
              branchCount={branchCount}
              activatedAt={draftData?.lastActivatedAt}
              updatedAt={draftData?.updatedAt}
            />
          )}

          {networks.flatMap(network => renderNodeCards(network))}
        </div>
      </div>
    </div>
  );
}

export default DraftDetails;
